import { useCallback, useEffect, useState, type ReactNode } from "react";
import clsx from "clsx";
import { ChevronDown, Mail, RefreshCw, Send } from "lucide-react";
import { Modal } from "@/components/ui/Modal";
import { Button } from "@/components/ui/Button";
import { Alert } from "@/components/ui/Alert";
import { api } from "@/lib/apiClient";
import { formatAuditTimestamp } from "@/lib/auditFormat";
import { sendInquiryEmail } from "@/lib/bookingInquiryActions";

export interface InquiryEmailPreview {
  to_address: string;
  from_address: string;
  from_name: string | null;
  subject: string;
  body_text: string;
  body_html: string;
}

export interface SentEmailItem {
  id: string;
  kind: string;
  from_address: string;
  from_name: string | null;
  to_address: string;
  subject: string;
  body_text: string;
  body_html: string;
  provider: string;
  actor_login: string | null;
  created_at: string;
}

export type InquiryResponseIntent = "confirm" | "reject";

export type InquiryEmailVariant = "acceptance" | "rejection";

export function inquiryIntentToVariant(intent: InquiryResponseIntent): InquiryEmailVariant {
  return intent === "confirm" ? "acceptance" : "rejection";
}

export interface InquiryEmailModalProps {
  open: boolean;
  bookingId: string | null;
  bookingNumber?: string | null;
  intent: InquiryResponseIntent;
  canEdit?: boolean;
  confirmLoading?: boolean;
  rejectLoading?: boolean;
  onClose: () => void;
  onSent?: () => void;
  onConfirm?: (options?: { skipConfirmationEmail?: boolean }) => void | Promise<void>;
  onReject?: () => void | Promise<void>;
}

const fieldClassName =
  "w-full bg-transparent py-2.5 text-sm text-label outline-none placeholder:text-label-tertiary disabled:cursor-not-allowed";

function MailRow({
  label,
  htmlFor,
  editable = false,
  children,
}: {
  label: string;
  htmlFor?: string;
  editable?: boolean;
  children: ReactNode;
}) {
  return (
    <div
      className={clsx(
        "grid shrink-0 grid-cols-[4.75rem_minmax(0,1fr)] items-center gap-2 border-b border-separator px-4",
        editable &&
          "transition-colors hover:bg-fill-quaternary/80 focus-within:bg-accent-soft/30 focus-within:shadow-[inset_2px_0_0_0_var(--eh-accent)]",
      )}
    >
      <label
        htmlFor={htmlFor}
        className="text-[0.68rem] font-semibold uppercase tracking-wider text-label-tertiary"
      >
        {label}
      </label>
      {children}
    </div>
  );
}

function ComposerSkeleton() {
  return (
    <div
      className="overflow-hidden rounded-2xl border border-separator"
      aria-busy="true"
      aria-label="Lade E-Mail-Vorschau"
    >
      <div className="space-y-2 border-b border-separator px-4 py-4">
        <div className="h-4 w-28 animate-pulse rounded bg-fill-tertiary" />
        <div className="h-3 w-72 max-w-full animate-pulse rounded bg-fill-quaternary" />
      </div>
      {["an", "von", "betreff"].map((row) => (
        <div
          key={row}
          className="grid grid-cols-[4.75rem_minmax(0,1fr)] items-center gap-2 border-b border-separator px-4 py-3.5"
        >
          <div className="h-3 w-10 animate-pulse rounded bg-fill-quaternary" />
          <div className="h-3 animate-pulse rounded bg-fill-tertiary" />
        </div>
      ))}
      <div className="h-52 animate-pulse bg-fill-quaternary/50" />
    </div>
  );
}

export function InquiryEmailModal({
  open,
  bookingId,
  bookingNumber,
  intent,
  canEdit = true,
  confirmLoading = false,
  rejectLoading = false,
  onClose,
  onSent,
  onConfirm,
  onReject,
}: InquiryEmailModalProps) {
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [preview, setPreview] = useState<InquiryEmailPreview | null>(null);
  const [toAddress, setToAddress] = useState("");
  const [subject, setSubject] = useState("");
  const [bodyText, setBodyText] = useState("");
  const [sentEmails, setSentEmails] = useState<SentEmailItem[]>([]);
  const [expandedEmailId, setExpandedEmailId] = useState<string | null>(null);
  const [historyOpen, setHistoryOpen] = useState(false);

  const variant = inquiryIntentToVariant(intent);
  const inquiryActionBusy = sending || confirmLoading || rejectLoading;
  const showInquiryActions = canEdit && (onConfirm || onReject);
  const hasRecipient = Boolean(toAddress.trim());
  const canSendEmail = hasRecipient && Boolean(subject.trim()) && Boolean(bodyText.trim());
  const draftDirty =
    preview !== null &&
    (toAddress !== preview.to_address ||
      subject !== preview.subject ||
      bodyText !== preview.body_text);
  const fieldsLocked = inquiryActionBusy || loading;

  const load = useCallback(async () => {
    if (!bookingId) return;
    setLoading(true);
    setError(null);
    setSuccess(false);
    try {
      const [previewResult, historyResult] = await Promise.all([
        api.get<InquiryEmailPreview>(
          `/api/v1/bookings/${bookingId}/inquiry-email/preview?variant=${variant}`,
        ),
        api.get<SentEmailItem[]>(`/api/v1/bookings/${bookingId}/sent-emails`),
      ]);
      setPreview(previewResult);
      setToAddress(previewResult.to_address);
      setSubject(previewResult.subject);
      setBodyText(previewResult.body_text);
      setSentEmails(historyResult);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Vorschau konnte nicht geladen werden.");
      setPreview(null);
    } finally {
      setLoading(false);
    }
  }, [bookingId, variant]);

  useEffect(() => {
    if (!open || !bookingId) return;
    void load();
  }, [open, bookingId, load]);

  useEffect(() => {
    if (open) return;
    setHistoryOpen(false);
    setExpandedEmailId(null);
  }, [open]);

  async function onSend() {
    if (!bookingId || !preview || !canSendEmail) return;
    setSending(true);
    setError(null);
    setSuccess(false);
    try {
      await sendInquiryEmail(bookingId, {
        to_address: toAddress,
        subject: preview && subject !== preview.subject ? subject : null,
        body_text: preview && bodyText !== preview.body_text ? bodyText : null,
        variant,
      });
      setSuccess(true);
      onSent?.();
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : "E-Mail konnte nicht gesendet werden.");
    } finally {
      setSending(false);
    }
  }

  async function onConfirmOrRejectWithEmail() {
    if (!bookingId || !preview || inquiryActionBusy) return;
    setSending(true);
    setError(null);
    setSuccess(false);
    try {
      if (canSendEmail) {
        await sendInquiryEmail(bookingId, {
          to_address: toAddress,
          subject: preview && subject !== preview.subject ? subject : null,
          body_text: preview && bodyText !== preview.body_text ? bodyText : null,
          variant,
        });
        setSuccess(true);
        onSent?.();
      }
      if (intent === "confirm" && onConfirm) {
        await onConfirm({ skipConfirmationEmail: canSendEmail });
      } else if (intent === "reject" && onReject) {
        await onReject();
      }
    } catch (e) {
      setError(
        e instanceof Error
          ? e.message
          : intent === "confirm"
            ? "Bestätigung fehlgeschlagen."
            : "Ablehnung fehlgeschlagen.",
      );
    } finally {
      setSending(false);
    }
  }

  const fromLabel = preview
    ? preview.from_name
      ? `${preview.from_name} <${preview.from_address}>`
      : preview.from_address
    : "—";

  const title = intent === "confirm" ? "Buchungsbestätigung" : "Absage per E-Mail";
  const description = bookingNumber
    ? `Anfrage ${bookingNumber} · Entwurf vor dem Versand anpassen.`
    : "Entwurf vor dem Versand anpassen.";

  const confirmActionLabel = canSendEmail
    ? "Bestätigen und Buchungsbestätigung senden"
    : "Anfrage bestätigen";
  const rejectActionLabel = canSendEmail ? "Ablehnen und E-Mail senden" : "Anfrage ablehnen";

  const footerHint =
    loading && !preview
      ? "Entwurf wird geladen…"
      : showInquiryActions
        ? canSendEmail
          ? intent === "confirm"
            ? "Die Buchungsbestätigung geht so raus und die Anfrage wird bestätigt."
            : "Die Mail geht so raus und die Anfrage wird abgelehnt."
          : "Ohne Empfänger wird nur die Anfrage bearbeitet, ohne E-Mail."
        : hasRecipient
          ? "Die Mail wird mit dem aktuellen Entwurf versendet."
          : "Bitte geben Sie eine Empfänger-Adresse ein, um die E-Mail zu versenden.";

  const sendAction =
    showInquiryActions && intent === "confirm" && onConfirm ? (
      <Button
        type="button"
        loading={inquiryActionBusy}
        disabled={inquiryActionBusy || loading || !preview}
        leftIcon={<Send className="h-4 w-4" aria-hidden />}
        onClick={() => void onConfirmOrRejectWithEmail()}
      >
        {confirmActionLabel}
      </Button>
    ) : showInquiryActions && intent === "reject" && onReject ? (
      <Button
        type="button"
        variant="danger"
        loading={inquiryActionBusy}
        disabled={inquiryActionBusy || loading || !preview}
        leftIcon={<Send className="h-4 w-4" aria-hidden />}
        onClick={() => void onConfirmOrRejectWithEmail()}
      >
        {rejectActionLabel}
      </Button>
    ) : !showInquiryActions && preview ? (
      <Button
        type="button"
        loading={sending}
        disabled={!canSendEmail || inquiryActionBusy}
        leftIcon={<Send className="h-4 w-4" aria-hidden />}
        onClick={() => void onSend()}
      >
        E-Mail senden
      </Button>
    ) : null;

  const footer = (
    <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
      <p className="text-xs leading-relaxed text-label-secondary sm:max-w-xs">{footerHint}</p>
      <div className="flex flex-wrap justify-end gap-2">
        <Button type="button" variant="ghost" onClick={onClose}>
          Schließen
        </Button>
        {sendAction}
      </div>
    </div>
  );

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={title}
      description={description}
      size="xl"
      footer={footer}
      className="eh-modal-panel--tall max-sm:!h-[96dvh] max-sm:!max-h-[96dvh] sm:!w-[min(76rem,calc(100vw-2rem))] sm:!max-w-[min(76rem,calc(100vw-2rem))]"
      bodyClassName="flex min-h-0 flex-col"
    >
      <div className="flex min-h-0 flex-1 flex-col gap-4">
        {error ? (
          <Alert variant="error" className="shrink-0">
            {error}
          </Alert>
        ) : null}
        {success ? (
          <Alert variant="success" className="shrink-0">
            E-Mail wurde gesendet und gespeichert.
          </Alert>
        ) : null}

        {loading && !preview ? <ComposerSkeleton /> : null}

        {preview ? (
          <div
            className={clsx(
              "flex min-h-0 flex-1 flex-col overflow-hidden rounded-2xl border border-separator bg-card shadow-ios",
              loading && "pointer-events-none opacity-70",
            )}
          >
            <div className="flex shrink-0 items-start gap-3 border-b border-separator bg-fill-quaternary/70 px-4 py-3.5">
              <span
                className={clsx(
                  "mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full",
                  intent === "confirm"
                    ? "bg-accent-soft text-accent"
                    : "bg-status-red/15 text-status-red",
                )}
              >
                <Mail className="h-4 w-4" aria-hidden />
              </span>
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <p className="text-sm font-semibold text-label">Entwurf</p>
                  <span
                    className={clsx(
                      "rounded-full px-2 py-0.5 text-[0.65rem] font-medium",
                      draftDirty
                        ? "bg-status-orange/15 text-status-orange"
                        : "bg-fill-tertiary text-label-secondary",
                    )}
                  >
                    {draftDirty ? "Geändert" : "Vorlage"}
                  </span>
                  {bookingNumber ? (
                    <span className="rounded-full bg-accent-soft px-2 py-0.5 text-[0.65rem] font-medium text-accent">
                      {bookingNumber}
                    </span>
                  ) : null}
                </div>
                <p className="mt-0.5 text-xs leading-relaxed text-label-secondary">
                  Absender ist fest. Empfänger, Betreff und Nachricht können Sie noch ändern.
                </p>
              </div>
            </div>

            <MailRow label="An" htmlFor="inquiry-email-to" editable>
              <input
                id="inquiry-email-to"
                type="email"
                autoComplete="email"
                className={fieldClassName}
                value={toAddress}
                onChange={(e) => setToAddress(e.target.value)}
                disabled={fieldsLocked}
                placeholder="E-Mail-Adresse des Gastes"
              />
            </MailRow>

            <MailRow label="Von">
              <p className="break-all py-2.5 text-sm text-label-secondary">{fromLabel}</p>
            </MailRow>

            <MailRow label="Betreff" htmlFor="inquiry-email-subject" editable>
              <input
                id="inquiry-email-subject"
                type="text"
                className={fieldClassName}
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                disabled={fieldsLocked}
                placeholder="Betreff"
              />
            </MailRow>

            <div className="flex min-h-[16rem] flex-1 flex-col px-4 pb-2 pt-3">
              <label
                htmlFor="inquiry-email-body"
                className="text-[0.68rem] font-semibold uppercase tracking-wider text-label-tertiary"
              >
                Nachricht
              </label>
              <textarea
                id="inquiry-email-body"
                className="mt-1 h-0 min-h-0 w-full flex-1 resize-y bg-transparent text-[0.9375rem] leading-6 text-label outline-none placeholder:text-label-tertiary disabled:cursor-not-allowed"
                value={bodyText}
                onChange={(e) => setBodyText(e.target.value)}
                disabled={fieldsLocked}
                placeholder="Nachricht an den Gast…"
              />
            </div>

            <div className="flex shrink-0 flex-col gap-2 border-t border-separator bg-fill-quaternary/50 px-4 py-2.5 sm:flex-row sm:items-center sm:justify-between">
              <p className="text-xs leading-relaxed text-label-secondary">
                {intent === "confirm" ? (
                  <>
                    Anrede, Buchungsdaten und das PDF hängen an dieser Nachricht. Das ist die Zusage an den Gast.
                  </>
                ) : (
                  <>
                    Anrede und Grußformel werden automatisch ergänzt. Platzhalter wie{" "}
                    <code className="rounded bg-fill px-1 py-0.5 font-mono text-[0.7rem]">{`{{booking_number}}`}</code>{" "}
                    werden beim Versand ersetzt.
                  </>
                )}
              </p>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="shrink-0 self-start sm:self-center"
                disabled={fieldsLocked}
                loading={loading}
                leftIcon={<RefreshCw className="h-3.5 w-3.5" aria-hidden />}
                onClick={() => void load()}
                title={
                  draftDirty
                    ? "Setzt Empfänger, Betreff und Nachricht auf die Vorlage zurück."
                    : "Vorlage erneut laden"
                }
              >
                Vorlage neu laden
              </Button>
            </div>
          </div>
        ) : null}

        {sentEmails.length > 0 ? (
          <section className="shrink-0 overflow-hidden rounded-xl border border-separator">
            <button
              type="button"
              className="flex w-full items-center gap-2 px-4 py-3 text-left transition-colors hover:bg-fill-quaternary"
              onClick={() => setHistoryOpen((current) => !current)}
              aria-expanded={historyOpen}
            >
              <ChevronDown
                className={clsx(
                  "h-4 w-4 shrink-0 text-label-tertiary transition-transform",
                  historyOpen && "rotate-180",
                )}
                aria-hidden
              />
              <span className="text-sm font-medium text-label">Gesendete E-Mails</span>
              <span className="rounded-full bg-fill-tertiary px-2 py-0.5 text-xs tabular-nums text-label-secondary">
                {sentEmails.length}
              </span>
            </button>
            {historyOpen ? (
              <ul className="space-y-2 border-t border-separator px-3 py-3">
                {sentEmails.map((item) => {
                  const expanded = expandedEmailId === item.id;
                  return (
                    <li key={item.id} className="rounded-lg bg-fill-quaternary/60 px-3 py-2">
                      <button
                        type="button"
                        className="w-full text-left"
                        onClick={() => setExpandedEmailId(expanded ? null : item.id)}
                        aria-expanded={expanded}
                      >
                        <div className="flex flex-wrap items-baseline justify-between gap-2">
                          <span className="text-sm font-medium text-label">{item.subject}</span>
                          <span className="text-xs text-label-secondary">
                            {formatAuditTimestamp(item.created_at)}
                          </span>
                        </div>
                        <p className="text-xs text-label-secondary">
                          An {item.to_address}
                          {item.actor_login ? ` · ${item.actor_login}` : ""}
                        </p>
                      </button>
                      {expanded ? (
                        <pre className="mt-2 max-h-48 overflow-auto whitespace-pre-wrap rounded-lg bg-card p-3 text-xs leading-relaxed text-label">
                          {item.body_text}
                        </pre>
                      ) : null}
                    </li>
                  );
                })}
              </ul>
            ) : null}
          </section>
        ) : null}
      </div>
    </Modal>
  );
}
